import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Mail, Shield, Lock, Eye } from 'lucide-react'

interface NewGmailConnectionProps {
  account: {
    id: number
    name: string
  }
}

export default function NewGmailConnection({ account }: NewGmailConnectionProps) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <Mail className="w-16 h-16 mx-auto text-blue-600 dark:text-blue-400 mb-4" />
          <h1 className="text-3xl font-bold text-blue-950 dark:text-white mb-2">
            Connect Gmail to {account.name}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            FilterHawk needs access to your Gmail account to help you manage your emails
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>What we'll access</CardTitle>
            <CardDescription>
              FilterHawk will request the following permissions from Google
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Eye className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Read Gmail labels</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    View your existing Gmail labels to help organize your emails
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Read emails</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Access your emails to apply filters and automation (coming soon)
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Your privacy matters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 mt-0.5 text-green-600" />
                <p>We encrypt your Gmail tokens using industry-standard encryption</p>
              </div>
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 mt-0.5 text-green-600" />
                <p>We only access the data necessary to provide our service</p>
              </div>
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 mt-0.5 text-green-600" />
                <p>You can disconnect your account at any time</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-4">
          <Button asChild size="lg" className="flex-1">
            <a href="/gmail_connections/connect">
              <Mail className="w-4 h-4 mr-2" />
              Connect Gmail Account
            </a>
          </Button>
          <Button asChild variant="outline" size="lg">
            <a href="/">Cancel</a>
          </Button>
        </div>
      </div>
    </div>
  )
}
